import { useCategories } from "@/hooks/categories/useCategories";
import { useNiches } from "@/hooks/niches/useNiches";
import { Dropdown, Icon, InputText, InputTextarea } from "@uigovpe/components";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import type { ProductFormValues } from "../types";

const MAX_DESCRIPTION = 120;

export default function ProductBasicInfo({ onLoadingChange }: { onLoadingChange?: (loading: boolean) => void }) {
  const {
    clearErrors,
    control,
    formState: { errors },
    register,
    setValue,
    watch,
  } = useFormContext<ProductFormValues>();
  const { categoryOptions: categories, error: categoryError, isLoading: loadingCategories, loadCategories } = useCategories();
  const { clearNiches, error: nicheError, isLoading: loadingNiches, loadNiches, nicheOptions: niches } = useNiches();
  const category = watch("category");

  useEffect(() => {
    void loadCategories().catch(() => undefined);
  }, [loadCategories]);

  useEffect(() => {
    onLoadingChange?.(loadingCategories);
  }, [loadingCategories, onLoadingChange]);

  useEffect(() => {
    clearNiches();
    if (category) void loadNiches(category).catch(() => undefined);
  }, [category, clearNiches, loadNiches]);

  return (
    <section className="flex! flex-col! gap-5!">
      <div>
        <div className="flex! items-center! gap-2! text-base! font-bold! text-zinc-950!">
          <Icon icon="person" className="text-[#4e3873]!" />
          <h2 className="m-0!">Produto</h2>
        </div>
        <p className="mt-2! text-sm! text-[#28272c]!">
          Informe os dados principais do produto ou serviço.
        </p>
      </div>
      <InputText
        label="Nome do produto ou serviço"
        placeholder="Ex: Bolos Personalizados"
        supportText={
          errors.name?.message ??
          "Use um nome claro para facilitar a busca e o entendimento do que você oferece"
        }
        invalid={Boolean(errors.name)}
        className="w-full!"
        {...register("name", {
          required: "Informe o nome do produto ou serviço.",
        })}
      />
      <div className="grid! grid-cols-1! gap-6! md:grid-cols-2!">
        <Controller
          name="category"
          control={control}
          rules={{ required: "Selecione uma categoria." }}
          render={({ field, fieldState }) => (
            <Dropdown
              label="Categoria"
              value={field.value}
              options={categories}
              placeholder={
                loadingCategories
                  ? "Carregando categorias..."
                  : "Selecione uma categoria"
              }
              supportText={fieldState.error?.message ?? categoryError}
              invalid={Boolean(fieldState.error)}
              disabled={loadingCategories}
              className="w-full!"
              onBlur={field.onBlur}
              onChange={(event) => {
                const value = String(event.value ?? "");
                setValue("category", value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue(
                  "categoryLabel",
                  categories.find((item) => item.value === value)?.label ?? "",
                );
                setValue("subcategory", "");
                setValue("subcategoryLabel", "");
                if (value) clearErrors("category");
              }}
            />
          )}
        />
        <Controller
          name="subcategory"
          control={control}
          rules={{ required: "Selecione uma subcategoria." }}
          render={({ field, fieldState }) => (
            <Dropdown
              label="Subcategoria"
              value={field.value}
              options={niches}
              placeholder={
                loadingNiches
                  ? "Carregando subcategorias..."
                  : category
                    ? "Selecione uma subcategoria"
                    : "Selecione uma categoria primeiro"
              }
              supportText={fieldState.error?.message ?? nicheError}
              invalid={Boolean(fieldState.error)}
              disabled={!category || loadingNiches}
              className="w-full!"
              onBlur={field.onBlur}
              onChange={(event) => {
                const value = String(event.value ?? "");
                setValue("subcategory", value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue(
                  "subcategoryLabel",
                  niches.find((item) => item.value === value)?.label ?? "",
                );
                if (value) clearErrors("subcategory");
              }}
            />
          )}
        />
      </div>
      <Controller
        name="description"
        control={control}
        rules={{
          required: "Informe a descrição do produto.",
          minLength: {
            value: 20,
            message: "Descreva o produto com pelo menos 20 caracteres.",
          },
          maxLength: {
            value: MAX_DESCRIPTION,
            message: `A descrição deve ter no máximo ${MAX_DESCRIPTION} caracteres.`,
          },
        }}
        render={({ field, fieldState }) => (
          <div className="w-full!">
            <InputTextarea
              label="Descrição do produto"
              placeholder="Descreva o que você oferece, seus diferenciais, materiais, benefícios e outras informações importantes"
              rows={7}
              value={field.value}
              invalid={Boolean(fieldState.error)}
              maxLength={MAX_DESCRIPTION}
              className="w-full! resize-none!"
              onBlur={field.onBlur}
              onChange={(event) =>
                field.onChange(event.target.value.slice(0, MAX_DESCRIPTION))
              }
            />
            <div className="mt-1! flex! items-start! justify-between! gap-3! text-xs! text-zinc-700!">
              <span className={fieldState.error ? "text-red-700!" : ""}>
                {fieldState.error?.message ??
                  "Esse texto ajuda visitantes a entenderem melhor seu produto ou serviço"}
              </span>
              <span className="ml-auto! shrink-0!">
                {field.value.length}/{MAX_DESCRIPTION} caracteres
              </span>
            </div>
          </div>
        )}
      />
    </section>
  );
}
